import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import AsyncSelect from 'react-select/async';
import PropTypes from 'prop-types';

import { MAPBOX_FORWARD_GEOCODING_URL } from 'utils/mapbox';
import styles from './styles.module.scss';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;
const MAPBOX_DEBOUNCE_DELAY = 400;
const MIN_SYMBOL = 2;

function CountrySelect({
  name,
  placeholder,
  value,
  onChange,
  isMulti,
  isClearable,
  noOptionsMessage,
  className,
}) {
  const lang = useSelector((state) => state.settings.lang);

  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const searchTimeoutRef = useRef(null);
  const mapboxAbortRef = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (mapboxAbortRef.current) {
        mapboxAbortRef.current.abort();
      }
    };
  }, []);

  const formatCountryFeatures = (features) => {
    return features.map((feature) => {
      const coords = feature.geometry?.coordinates || [0, 0];
      const countryName = feature.properties?.name || feature.properties?.full_address || feature.place_name || '';
      const countryCode = feature.properties?.context?.country?.country_code || feature.properties?.short_code?.toUpperCase() || '';

      return {
        value: {
          mapbox_id: feature.properties?.mapbox_id || feature.id,
          country_code: countryCode,
          country: countryName,
          full_address: countryName,
          location: {
            longitude: coords[0],
            latitude: coords[1],
          },
        },
        label: countryName,
        data: feature,
      };
    });
  };

  const loadOptions = (searchValue, callback) => {
    setInputValue(searchValue);
    if (searchValue.length <= MIN_SYMBOL) {
      callback([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    if (!MAPBOX_ACCESS_TOKEN) {
      console.warn('Missing Mapbox access token');
      callback([]);
      setIsLoading(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (mapboxAbortRef.current) {
      mapboxAbortRef.current.abort();
    }

    searchTimeoutRef.current = setTimeout(() => {
      const params = new URLSearchParams({
        q: searchValue,
        access_token: MAPBOX_ACCESS_TOKEN,
        language: lang,
        limit: '10',
        types: 'country',
      });

      mapboxAbortRef.current = new AbortController();

      fetch(`${MAPBOX_FORWARD_GEOCODING_URL}?${params.toString()}`, {
        signal: mapboxAbortRef.current.signal,
      })
        .then((response) => response.json())
        .then((result) => {
          if (result?.features?.length) {
            callback(formatCountryFeatures(result.features));
            return;
          }
          callback([]);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            callback([]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }, MAPBOX_DEBOUNCE_DELAY);
  };

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    control: (provided) => ({
      ...provided,
      borderRadius: '10px',
      border: '1px solid #959baa',
      minHeight: '42px',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e8f0fe',
      borderRadius: '4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1a73e8',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1a73e8',
      ':hover': {
        backgroundColor: '#d2e3fc',
        color: '#174ea6',
      },
    }),
  };

  return (
    <div className={className}>
      <AsyncSelect
        name={name}
        cacheOptions
        isLoading={isLoading}
        value={value}
        isMulti={isMulti}
        isClearable={isClearable}
        loadingMessage={() => 'Загрузка...'}
        noOptionsMessage={() => (inputValue.length > MIN_SYMBOL ? noOptionsMessage : null)}
        placeholder={placeholder}
        classNamePrefix="select"
        loadOptions={loadOptions}
        onChange={onChange}
        styles={customStyles}
        className={styles.select}
        components={{
          DropdownIndicator: null,
        }}
      />
    </div>
  );
}

CountrySelect.propTypes = {
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  className: PropTypes.string,
};

CountrySelect.defaultProps = {
  placeholder: 'Начните вводить',
  value: null,
  isMulti: false,
  isClearable: true,
  noOptionsMessage: 'Ничего не найдено',
  className: '',
};

export default CountrySelect;
